/**
 * C11 docking — the decision, as pure functions (seam S6, decision D-2).
 *
 * A surface that is not ready to be rewritten still belongs behind the one
 * door. It docks: the shell keeps the rail, the URL and the back-path, and the
 * surface renders inside the module viewport as an iframe carrying the Access
 * cookie the person already holds.
 *
 * The rule this file exists to enforce: **a surface that cannot satisfy the
 * docking contract is NAMED, never half-docked.** A rail entry that opens a
 * blank viewport is worse than no rail entry — it makes the portal look broken
 * for a surface that is fine, and it hides the one thing its owner needs to
 * read (what is missing).
 *
 * The API refuses an unsatisfiable manifest at registration, so this resolver
 * is the second line, not the only one. It is here because the shell must not
 * trust a control-plane response to be well-formed: an old row, a partial
 * migration or a hand-edited registry all reach the browser as data, and the
 * honest render for each is the same refusal with the reason in it.
 */

/**
 * The one Cloudflare Access application's cookie domain (`infra/README.md`).
 *
 * This is the whole security argument for docking in one constant. The Access
 * cookie is issued on `.ketosource.co`, so a framed surface on that domain
 * receives the identity the person already proved. A surface anywhere else
 * receives no cookie and renders its own sign-in inside our chrome — which is
 * exactly the second login BR-6 forbids.
 */
export const ACCESS_COOKIE_DOMAIN = ".ketosource.co";

/** The portal's own origin — the only origin a docked surface may allow to frame it. */
export const PORTAL_ORIGIN = "https://os.ketosource.co";

export interface DockedTab {
  tab_key: string;
  /** Where this tab lands inside the surface. Absent opens the surface itself. */
  surface_path?: string | null;
}

export interface DockableModule {
  module_key: string;
  display_name: string;
  kind?: "native" | "docked" | string | null;
  surface_url?: string | null;
  tabs?: DockedTab[];
}

/** What the viewport should render for a docked module. */
export type DockResolution =
  | {
      kind: "embed";
      /** The exact iframe `src`, tab path already applied. */
      src: string;
      /** Accessible name for the frame — the module's own name, never invented. */
      title: string;
      /** Always shown. A docked surface is never presented as native (BR-10). */
      label: string;
    }
  | {
      kind: "refused";
      /** Machine reason, for tests and logs. */
      reason: "not_docked" | "no_surface" | "off_access_application" | "unparseable_surface";
      /** The sentence a person reads. Names the module and what is wrong. */
      message: string;
    };

/** The label a docked surface always carries (UX v0.3 §8 Q-2). */
export const DOCKED_LABEL = "Older surface — being migrated";

/**
 * Is this host covered by the one Access application?
 *
 * Exact-suffix, and `ketosource.co` itself counts. Written as a suffix test on
 * the parsed hostname rather than on the URL string, because
 * `https://evil.example/?x=.ketosource.co` passes a string test and is not our
 * domain at all.
 */
export function onAccessApplication(host: string): boolean {
  const h = host.toLowerCase();
  const bare = ACCESS_COOKIE_DOMAIN.slice(1);
  return h === bare || h.endsWith(ACCESS_COOKIE_DOMAIN);
}

/**
 * Resolve what to render for `module` on `tabKey`.
 *
 * `tabKey` is the tab from the URL, so `/app/{area}/{module}/{tab}` deep-links
 * INTO the docked surface rather than always its home page. A tab that
 * declares no `surface_path` opens the surface itself — the resolver never
 * guesses a path, because a guessed path deep-links people to a 404 on an
 * application this portal does not own.
 */
export function resolveDock(
  module: DockableModule,
  tabKey: string | null = null,
  at: string | null = null,
): DockResolution {
  const name = module.display_name;

  if (module.kind !== "docked") {
    return {
      kind: "refused",
      reason: "not_docked",
      message: `${name} is not a docked surface.`,
    };
  }

  if (!module.surface_url) {
    return {
      kind: "refused",
      reason: "no_surface",
      message: `${name} is docked but has no surface to open. Its manifest needs a surface URL.`,
    };
  }

  let url: URL;
  try {
    url = new URL(module.surface_url);
  } catch {
    return {
      kind: "refused",
      reason: "unparseable_surface",
      message: `${name} declares a surface we can't read: ${module.surface_url}.`,
    };
  }

  if (url.protocol !== "https:" || !onAccessApplication(url.hostname)) {
    return {
      kind: "refused",
      reason: "off_access_application",
      message:
        `${name} can't be docked: ${url.origin} is outside the one sign-in, so opening it ` +
        `here would ask you to sign in again. It has to move onto a ${ACCESS_COOKIE_DOMAIN.slice(1)} ` +
        "host on the Access application first.",
    };
  }

  const tab = tabKey ? module.tabs?.find((t) => t.tab_key === tabKey) : undefined;
  const path = tab?.surface_path ?? null;
  // Resolved against the declared surface, so a path can deepen the URL but
  // never leave it: `new URL("//elsewhere.com", surface)` and friends are
  // re-checked below rather than trusted.
  const src = path ? new URL(path, url).toString() : url.toString();
  const resolved = new URL(src);
  if (resolved.origin !== url.origin) {
    return {
      kind: "refused",
      reason: "off_access_application",
      message: `${name} declares a tab path that leaves its own surface (${resolved.origin}).`,
    };
  }

  // `at` is a per-visit deep path INSIDE the surface — the artefact, report or
  // record a saved link actually named. Without it a docked module can only
  // ever open its own front page, so every old deep link on the six cut-off
  // hosts silently lost its tail and landed people on a list to search
  // (bug 3d40402f: a Canvas share link opened the library grid, not the item).
  //
  // Trust boundary: `at` comes from the URL bar, so unlike `surface_path` it is
  // NOT registry data and a person can type anything into it. It is resolved
  // against the surface and re-checked, and a value that leaves the origin is
  // IGNORED rather than refused — a mistyped or hostile link is not the
  // module's fault, and blanking the viewport for it would make a working
  // surface look broken. The person lands on the module home page, exactly
  // where they landed before this existed.
  if (at) {
    const deep = new URL(at, resolved);
    if (deep.origin === resolved.origin) {
      return { kind: "embed", src: deep.toString(), title: name, label: DOCKED_LABEL };
    }
  }

  return { kind: "embed", src, title: name, label: DOCKED_LABEL };
}
